import SplitscreenOutlined from '@mui/icons-material/SplitscreenOutlined';
import ViewSidebarOutlined from '@mui/icons-material/ViewSidebarOutlined';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';

import type { ProjectPanelLayout } from './projectPanelLayoutModel';

export interface PanelLayoutToggleProps {
  /** Currently active layout (derived from the page's show/hide levers). */
  layout: ProjectPanelLayout;
  /** Apply a new layout. */
  onChange: (layout: ProjectPanelLayout) => void;
}

// Matches Verantos's own custom icon set (Figma "MUI for Figma" library,
// Custom Icons page — Action Menu Icons=Assistant Dominant / Splitscreen /
// Workspace Dominant), confirmed by rendering each Figma node directly:
// they're MUI's own `view_sidebar` glyph (mirrored for the two single-pane
// states) and `splitscreen` glyph rotated 90° — the stock icons, not custom
// artwork, so no hand-copied SVG paths are needed here.
const OPTIONS: ReadonlyArray<{
  value: ProjectPanelLayout;
  label: string;
  icon: typeof ViewSidebarOutlined;
  transform?: string;
}> = [
  { value: 'ra-only', label: 'Research Assistant only', icon: ViewSidebarOutlined },
  { value: 'split', label: 'Split view', icon: SplitscreenOutlined, transform: 'rotate(90deg)' },
  {
    value: 'artifacts-only',
    label: 'Workspace only',
    icon: ViewSidebarOutlined,
    transform: 'scaleX(-1)',
  },
];

/**
 * 3-way panel-layout control for the project header (OR-6123 F3): an icon
 * group toggling between Research-Assistant-only, split, and artifacts-only.
 *
 * The control sets a single logical {@link ProjectPanelLayout}; the page
 * translates that into its existing show/hide levers (RA store + content
 * collapse). The collapse-to-strip visuals + persistence are deferred to F4 —
 * here the control only drives basic show/hide.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const PanelLayoutToggle = ({ layout, onChange }: PanelLayoutToggleProps) => {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={layout}
      onChange={(_event, next: ProjectPanelLayout | null) => {
        // `null` fires when the active button is re-clicked; ignore it so the
        // layout is always one of the three valid states.
        if (next) onChange(next);
      }}
      aria-label="Panel layout"
      data-testid="project-panel-layout-toggle"
    >
      {OPTIONS.map(({ value, label, icon: Icon, transform }) => (
        <Tooltip key={value} title={label} enterDelay={500}>
          <ToggleButton
            value={value}
            aria-label={label}
            data-testid={`project-panel-layout-${value}`}
          >
            {/* ToggleButton's default icon color is `action.active` (54%
                black) — reads as washed-out grey against these solid-fill
                icons. `text.primary` (87% black) gives real contrast. */}
            <Icon
              fontSize="small"
              sx={{ color: 'text.primary', ...(transform && { transform }) }}
            />
          </ToggleButton>
        </Tooltip>
      ))}
    </ToggleButtonGroup>
  );
};
